import {
    BillScheduleBlockReason,
    getBillScheduleBlockReason,
} from "@common/helpers/domains/accounting/billScheduleEligibility";
import { getScheduledLinesAmount } from "@common/helpers/domains/accounting/scheduleDocumentLines";
import { isBillItemLine } from "@common/helpers/lineItems";
import {
    BillLineItemSchedule,
    BillSchedule,
} from "@common/schemas/domains/bills/bill-response.schema";
import { BillLineItemKind, BillStatus } from "@common/types/bill";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { ScheduleCardValues } from "@components/accounting/schedules/scheduleCardCopy";
import {
    InteractiveScheduleCellState,
    ScheduleUnavailableReason,
} from "@components/invoices/views/scheduleCellState";

type AppliedScheduleCellState = Extract<
    InteractiveScheduleCellState,
    { kind: "applied" }
>;

export type BillScheduleCellState =
    | InteractiveScheduleCellState
    | { kind: "partial"; scheduledLineCount: number }
    // A line under a whole-bill application: the schedule belongs to the
    // bill, so the line shows it but changes happen on the bill row.
    | { kind: "wholeBill"; schedule: ScheduleCardValues }
    | {
          kind: "unavailable";
          reason: ScheduleUnavailableReason | BillScheduleBlockReason;
      };

interface ScheduledBillLine {
    kind?: BillLineItemKind | null;
    schedule?: BillLineItemSchedule;
}

const UNNAMED_SCHEDULE = "Schedule";

function isWholeBillScheduled(
    schedule: BillSchedule | undefined,
): schedule is BillSchedule & { scheduleApplicationId: string } {
    return (
        schedule?.scope === "bill" && schedule.scheduleApplicationId !== null
    );
}

function toAppliedState(
    lineSchedule: BillLineItemSchedule,
    viaLineItemCount: number | undefined,
): AppliedScheduleCellState {
    return {
        kind: "applied",
        scheduleName: lineSchedule.scheduleName,
        scheduleApplicationId: lineSchedule.scheduleApplicationId,
        scheduleTemplateId: lineSchedule.scheduleTemplateId,
        termEnd: lineSchedule.termEnd,
        remainingAmount: lineSchedule.remainingAmount,
        status: lineSchedule.status,
        ...(viaLineItemCount !== undefined ? { viaLineItemCount } : {}),
    };
}

/**
 * Line-scoped applications show on the bill row only when one application
 * owns them, so the card has a single schedule to replace or remove.
 */
function resolveLineScopedBillSchedule(bill: {
    schedule?: BillSchedule;
    lines?: ScheduledBillLine[];
}): AppliedScheduleCellState | null {
    if (bill.schedule?.scope !== "lines" || !bill.lines?.length) {
        return null;
    }
    const itemLines = bill.lines.filter(isBillItemLine);
    const lineSchedules = itemLines.flatMap((line) =>
        line.schedule ? [line.schedule] : [],
    );
    const applicationIds = new Set(
        lineSchedules.map((schedule) => schedule.scheduleApplicationId),
    );
    if (lineSchedules.length === 0 || applicationIds.size !== 1) {
        return null;
    }
    const isSingleWholeLine =
        lineSchedules.length === itemLines.length && lineSchedules.length < 2;
    return toAppliedState(
        lineSchedules[0],
        isSingleWholeLine ? undefined : lineSchedules.length,
    );
}

export function resolveBillScheduleCellState(bill: {
    status: BillStatus;
    currency: string;
    total?: number;
    schedule?: BillSchedule;
    lines?: ScheduledBillLine[];
}): BillScheduleCellState {
    const schedule = bill.schedule;
    if (isWholeBillScheduled(schedule)) {
        return {
            kind: "applied",
            scheduleName: schedule.scheduleName ?? UNNAMED_SCHEDULE,
            scheduleApplicationId: schedule.scheduleApplicationId,
            scheduleTemplateId: schedule.scheduleTemplateId,
            termEnd: schedule.termEnd,
            remainingAmount: schedule.remainingAmount ?? 0,
            status: schedule.status ?? ScheduleApplicationStatus.active,
        };
    }
    const lineScopedSchedule = resolveLineScopedBillSchedule(bill);
    if (lineScopedSchedule) {
        return lineScopedSchedule;
    }
    if (schedule?.scope === "lines" && schedule.scheduledLineIds.length > 0) {
        return {
            kind: "partial",
            scheduledLineCount: schedule.scheduledLineIds.length,
        };
    }
    const blockReason = getBillScheduleBlockReason(bill);
    if (blockReason) {
        return { kind: "unavailable", reason: blockReason };
    }
    if (bill.total === 0) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.zeroAmount,
        };
    }
    return { kind: "add" };
}

export function resolveBillLineScheduleCellState(line: {
    kind?: BillLineItemKind | null;
    amount?: number;
    schedule?: BillLineItemSchedule;
    bill?: {
        status: BillStatus;
        currency: string;
        schedule?: BillSchedule;
    };
}): BillScheduleCellState {
    if (line.schedule) {
        return toAppliedState(line.schedule, undefined);
    }
    const billSchedule = line.bill?.schedule;
    if (isWholeBillScheduled(billSchedule)) {
        return {
            kind: "wholeBill",
            schedule: {
                name: billSchedule.scheduleName ?? UNNAMED_SCHEDULE,
                termEnd: billSchedule.termEnd,
                remainingAmount: billSchedule.remainingAmount ?? 0,
                status: billSchedule.status ?? ScheduleApplicationStatus.active,
            },
        };
    }
    // Tax and discount rows are splits of an item line; the backend schedules
    // item lines only.
    if (!isBillItemLine(line)) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.derivedLine,
        };
    }
    const billBlockReason = line.bill
        ? getBillScheduleBlockReason(line.bill)
        : null;
    if (billBlockReason) {
        return { kind: "unavailable", reason: billBlockReason };
    }
    if (line.amount === 0) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.zeroAmount,
        };
    }
    return { kind: "add" };
}

export function isBillScheduleCellInteractive(
    state: BillScheduleCellState,
): state is InteractiveScheduleCellState {
    return state.kind === "add" || state.kind === "applied";
}

/**
 * Whether a schedule owns this line's expense account — either the line itself
 * is scheduled, or the whole bill is (in which case apply rewrote *every*
 * line's override, but carries no per-line badge).
 *
 * The Schedule column and the Expense account lock both read this so they
 * cannot disagree about what "scheduled" means.
 */
export function isBillLineOwnedBySchedule(line: {
    schedule?: BillLineItemSchedule;
    bill?: { schedule?: BillSchedule };
}): boolean {
    return (
        line.schedule !== undefined || isWholeBillScheduled(line.bill?.schedule)
    );
}

export interface BillScheduleReplaceScope {
    amount: number;
    sourceLineIds?: string[];
}

/** What a replace on the bill row covers: the lines the current schedule owns, or the whole bill. */
export function resolveBillScheduleReplaceScope(options: {
    bill: {
        total: number;
        schedule?: Pick<BillSchedule, "scope">;
        lines?: Array<ScheduledBillLine & { id: string; amount: number }>;
    };
    scheduleApplicationId: string | null;
}): BillScheduleReplaceScope {
    const { bill, scheduleApplicationId } = options;
    if (bill.schedule?.scope !== "lines" || scheduleApplicationId === null) {
        return { amount: bill.total };
    }
    const scopedLines = (bill.lines ?? []).filter(
        (line) =>
            isBillItemLine(line) &&
            line.schedule?.scheduleApplicationId === scheduleApplicationId,
    );
    if (scopedLines.length === 0) {
        return { amount: bill.total };
    }
    return {
        sourceLineIds: scopedLines.map((line) => line.id),
        amount: getScheduledLinesAmount(scopedLines),
    };
}
